import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import {
  FacebookMessengerShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  PinterestShareButton,
  TwitterShareButton,
  // eslint-disable-next-line no-unused-vars -- used in commented WhatsApp share block
  WhatsappShareButton,
} from "react-share";
import { toast } from "react-toastify";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import styled from "styled-components";
import FacebookIcon from "../../assets/images/social/facebook.svg";
import LinkedinIcon from "../../assets/images/social/linkedin.svg";
import MessengerIcon from "../../assets/images/social/messenger.svg";
import PinterestIcon from "../../assets/images/social/pinterest.svg";
import TwitterIcon from "../../assets/images/social/twitter.svg";
// eslint-disable-next-line no-unused-vars -- used in commented WhatsApp share block
import WhatsappIcon from "../../assets/images/social/whatsapp.svg";
import { TextView } from "../../components";
import { colors } from "../../utils/theme";
import WheelComponent from "../../components/WheelComponent";
import {
  fetchImageAsBase64,
  removeHtmlTags,
  sliderToOpacity,
  transformEditorText,
} from "../../utils/helpers";
import { device } from "../../utils/device";
import useScreenSize from "../../hooks/useScreenSize";
import ContentEditable from "react-contenteditable";

const Container = styled.div`
  position: ${({ onGallery }) => (onGallery ? "fixed" : "absolute")};
  top: ${({ onGallery }) => (onGallery ? "50%" : "9vh")};
  right: ${({ onGallery }) => (onGallery ? "auto" : "6vw")};
  left: ${({ onGallery }) => (onGallery ? "50%" : "auto")};
  transform: ${({ onGallery }) =>
    onGallery ? "translate(-50%, -50%)" : "none"};
  background-color: ${colors.white};
  padding: 16px 0px;
  border-radius: 8px;
  z-index: 2000;
  width: 432px;
  box-shadow: 0px 0px 9.3px 0px #3b3b3b1c;

  @media ${device.mobilePortrait} {
    width: 100vw;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    z-index: 2000;
    border-radius: 0px 0px 16px 16px;
  }

  @media (${device.mobileLandscape}) {
    right: 1vw;
    top: 12vh;
  }
`;

const Outer = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background: #0000008a; /* Semi-transparent black overlay */
  z-index: 1999; /* Just below the modal's z-index */
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 0px 20px 20px 20px;
  border-bottom: 1px solid #d9d9d9;
`;

const IconGrid = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 24px;
  padding: 0 10px;

  @media (${device.mobilePortrait}) {
    justify-content: center;
  }
`;

const Icon = styled.img`
  width: 28px;
  height: 28px;
  margin: 0 10px;
  cursor: pointer;
  border-radius: 50%;

  @media (${device.mobilePortrait}) {
    width: 22px;
    height: 22px;
  }
`;

const CopyIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0 10px;
  cursor: pointer;
  border-radius: 50%;
  background-color: #f0f2f5;

  @media (${device.mobilePortrait}) {
    width: 22px;
    height: 22px;
  }
`;

const WheelPreview = styled.div`
  width: calc(100% - 20px); /* Adjusted width to account for margins */
  background-color: ${colors.background};
  padding: 16px;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 40% 60%;
  grid-gap: 20px;
  margin: 0px 10px; /* Consistent 10px margin on both sides */
  box-sizing: border-box; /* Ensure padding and borders are included in width calculation */

  @media (${device.mobilePortrait}) {
    grid-template-columns: 50% 50%;
    grid-gap: 10px;
  }

  ${({ isMobilePortrait }) =>
    isMobilePortrait &&
    `
      grid-template-columns: 45% 55%;
      grid-gap: 16px;
    `}
`;

const WheelBackground = styled.div`
  height: 120px;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  z-index: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (${device.mobilePortrait}) {
    height: 40vw;
    padding: 10px;
  }

  ${({ isMobilePortrait }) =>
    isMobilePortrait &&
    `
    height: 35vw;
    padding: 10px;
    `}
`;
const WheelDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const Background = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: -1;
  background: ${({ currentBg, bgWallpaper, color }) => {
    if ((currentBg === 2 || currentBg === 3) && bgWallpaper !== "") {
      return currentBg === 2 ? `url(${bgWallpaper})` : bgWallpaper;
    }
    return color;
  }};
  object-fit: contain;
  background-position: stretch;
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-size: 100% 100%;
  opacity: ${({ wallpaperTransparency }) =>
    sliderToOpacity(wallpaperTransparency)};
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ wallpaperHueColor }) =>
      wallpaperHueColor ?? "#ffffff"};
    opacity: ${({ wallpaperHuePercentage }) =>
      ((wallpaperHuePercentage ?? 0) / 100)};
    mix-blend-mode: multiply;
    pointer-events: none;
  }
`;

const ShareButton = ({
  ShareComponent,
  url,
  quote,
  media,
  description,
  IconSrc,
  appId,
  onGallery,
}) => (
  <ShareComponent
    url={url}
    title={quote}
    media={media}
    description={description}
    appId={appId} // Only used where applicable, e.g., Messenger
  >
    <Icon src={IconSrc} alt="share-icon" />
  </ShareComponent>
);

export default function ShareModal({
  wheel,
  handleClose,
  chartData,
  userData,
  wheelBackground,
  style,
  color,
  currentBg,
  bgWallpaper,
  wallpaperTransparency,
  wallpaperHuePercentage,
  wallpaperHueColor,
  onGallery,
}) {
  const quote = "Check out this amazing wheel!";
  console.log("wheeeeeeeeeel", wheel);
  const desc = wheel?.description || "A wheel worth sharing!";
  const currentUrl = wheel?.share_url;

  const { isMobilePortrait } = useScreenSize();

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(currentUrl)
      .then(() => {
        toast.success("URL copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy URL: ", err);
      });
  };

  const [base64Wallpaper, setBase64Wallpaper] = useState(null);

  useEffect(() => {
    const convertWallpaperToBase64 = async () => {
      if (bgWallpaper && bgWallpaper?.startsWith("http")) {
        try {
          console.log("curren0101011tBg", "TRY 64 bgWallpaper", bgWallpaper);
          const base64 = await fetchImageAsBase64(bgWallpaper);
          setBase64Wallpaper(base64);
        } catch (error) {
          console.error("Error converting wallpaper to base64:", error);
          setBase64Wallpaper(bgWallpaper);
          console.log("curren0101011tBg", "Catch 64 bgWallpaper");
        }
      } else {
        console.log("curren0101011tBg", "Else 64 bgWallpaper");

        setBase64Wallpaper(bgWallpaper);
      }
    };

    convertWallpaperToBase64();
  }, [bgWallpaper]);






  // Truncate description HTML by text content length while preserving HTML tags
  const truncateDescriptionHtml = (htmlString, maxLength = 100) => {
    if (!htmlString) return "";

    // Create a temporary div to parse HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    // Get all text content to check length
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    // If text is within limit, return original HTML
    if (textContent.length <= maxLength) {
      return htmlString;
    }

    // Function to recursively traverse and truncate text nodes
    const truncateTextNodes = (element, remainingLength) => {
      let truncated = false;
      let currentIndex = 0;

      while (currentIndex < element.childNodes.length && !truncated) {
        const node = element.childNodes[currentIndex];

        if (node.nodeType === Node.TEXT_NODE) {
          // Text node
          const text = node.textContent || "";
          if (remainingLength - text.length <= 0) {
            // Truncate this text node
            const truncatedText = text.substring(0, remainingLength).trim();
            node.textContent = truncatedText + "...";
            truncated = true;

            // Remove all subsequent siblings
            while (currentIndex + 1 < element.childNodes.length) {
              element.removeChild(element.childNodes[currentIndex + 1]);
            }
          } else {
            remainingLength -= text.length;
            currentIndex++;
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Element node - recurse into it
          const result = truncateTextNodes(node, remainingLength);
          remainingLength = result.remainingLength;
          if (result.truncated) {
            truncated = true;

            // Remove all subsequent siblings
            while (currentIndex + 1 < element.childNodes.length) {
              element.removeChild(element.childNodes[currentIndex + 1]);
            }
          } else {
            currentIndex++;
          }
        } else {
          currentIndex++;
        }
      }

      return { remainingLength, truncated };
    };

    // Start truncation from the root element
    truncateTextNodes(tempDiv, maxLength);

    return tempDiv.innerHTML;
  };

  const [showFullDesc, setShowFullDesc] = useState(false);
  const fullDescHtml = transformEditorText(wheel?.description || "");
  const plainDescText = removeHtmlTags(fullDescHtml || "");
  const isDescLong = plainDescText.length > 100;
  const truncatedDescHtml = truncateDescriptionHtml(fullDescHtml, 100);

  return (
    <>
      <Container style={style} onGallery={onGallery}>
        <Header>
          <TextView
            variant="subtitle"
            style={{
              color: "#363636",
              fontWeight: 700,
            }}
          >
            Share Wheel
          </TextView>
          <CloseIcon style={{ cursor: "pointer" }} onClick={handleClose} />
        </Header>

        <IconGrid>
          <ShareButton
            ShareComponent={FacebookShareButton}
            url={currentUrl}
            quote={quote}
            IconSrc={FacebookIcon}
            appId="1237547604287422" // Replace with your valid Facebook App ID
          />
          <ShareButton
            ShareComponent={PinterestShareButton}
            url={currentUrl}
            media={
              wheel?.wheel_preview ??
              "https://iyuga-backend.s3.us-east-1.amazonaws.com/wheel-share-image.jpeg"
            } // Pinterest requires a valid media URL
            description={desc}
            IconSrc={PinterestIcon}
          />
          <ShareButton
            ShareComponent={LinkedinShareButton}
            url={currentUrl}
            quote={quote}
            IconSrc={LinkedinIcon}
          />
          {/* <WhatsAppShareButton
            url={currentUrl}
            IconSrc={WhatsappIcon}
          /> */}

          {/* <ShareButton
            ShareComponent={WhatsappShareButton}
            url={currentUrl}
            IconSrc={WhatsappIcon}
          /> */}
          <ShareButton
            ShareComponent={TwitterShareButton}
            url={currentUrl}
            quote={quote}
            IconSrc={TwitterIcon}
          />
       <ShareButton
            ShareComponent={FacebookMessengerShareButton}
            url={currentUrl}
            appId="1237547604287422" // Replace with your valid Facebook App ID
            IconSrc={MessengerIcon}
          />
          <CopyIconWrapper onClick={copyToClipboard}>
            <ContentCopyIcon fontSize="18px" />
          </CopyIconWrapper>
        </IconGrid>

        <WheelPreview isMobilePortrait={isMobilePortrait}>
          <WheelBackground isMobilePortrait={isMobilePortrait}>
            <Background
              color={color}
              currentBg={currentBg}
              bgWallpaper={base64Wallpaper || bgWallpaper}
              wallpaperTransparency={wallpaperTransparency}
              wallpaperHuePercentage={wallpaperHuePercentage}
              wallpaperHueColor={wallpaperHueColor}
            />
            <WheelComponent
              config={{
                showTooltips: false,
              }}
              userData={{ id: Math.random() * 10, url: userData.url }}
              maxSelectedArcs={0}
              chartData={chartData}
              margin={1}
              containerDivStyle={{
                width: "100px",
                height: "100px",
              }}
            />
          </WheelBackground>
          <WheelDetails>
            <div
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
                hyphens: "auto",
                fontSize: "clamp(14px, 2vw, 18px)",
                lineHeight: 1.3,
                maxHeight: "2.6em",
                minHeight: "1.3em",
              }}
            >
              <ContentEditable
                html={transformEditorText(wheel?.wheel_name)}
                disabled
              />
            </div>
            <TextView
              variant="largeText"
              style={{ margin: "5px 0px 5px 0px" }}
            >{`By ${wheel?.user_profile?.first_name} ${wheel?.user_profile?.last_name}`}</TextView>
            <TextView style={{ paddingRight: "12px" }}>
              <div
                style={{
                  maxHeight: 110,
                  overflowY: "auto",
                  paddingRight: 8,
                }}
              >
                <ContentEditable
                  html={showFullDesc ? fullDescHtml : truncatedDescHtml}
                  disabled
                />
              </div>
              {isDescLong && (
                <div style={{ marginTop: 6 }}>
                  <button
                    onClick={() => setShowFullDesc((s) => !s)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#9B5AF8",
                      cursor: "pointer",
                      padding: 0,
                      fontSize: 13,
                    }}
                  >
                    {showFullDesc ? "Show less" : "Show more"}
                  </button>
                </div>
              )}
            </TextView>
          </WheelDetails>
        </WheelPreview>

        {/* Screenshot functionality moved to backend Lambda */}
      </Container>
      <Outer onClick={handleClose} isMobilePortrait={isMobilePortrait} />
    </>
  );
}
